import { useEffect, useMemo, useState } from "react";
import { t } from "ttag";
import _ from "underscore";

import {
  SettingsPageWrapper,
  SettingsSection,
} from "metabase/admin/components/SettingsSection";
import { StrategyForm } from "metabase/admin/performance/components/StrategyForm";
import { rootId } from "metabase/admin/performance/constants/simple";
import { useCacheConfigs } from "metabase/admin/performance/hooks/useCacheConfigs";
import { useSaveStrategy } from "metabase/admin/performance/hooks/useSaveStrategy";
import { SettingHeader } from "metabase/admin/settings/components/SettingHeader/SettingHeader";
import {
  BasicAdminSettingInput,
  SetByEnvVar,
} from "metabase/admin/settings/components/widgets/AdminSettingInput";
import { useAdminSetting } from "metabase/api/utils";
import { DelayedLoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper/DelayedLoadingAndErrorWrapper";
import { Button, Flex, Stack } from "metabase/ui";

export const CacheWriteSettings = () => {
  const minDuration = useAdminSetting("query-caching-min-duration-ms");
  const maxPayload = useAdminSetting("query-caching-max-kb");

  const [minDurationValue, setMinDurationValue] = useState<number | string>("");
  const [maxPayloadValue, setMaxPayloadValue] = useState<number | string>("");

  const {
    configs,
    error: configsError,
    isLoading: areConfigsLoading,
  } = useCacheConfigs({ model: ["root"] });

  const rootConfig = _.findWhere(configs ?? [], { model: "root" });
  const saveRootStrategy = useSaveStrategy(rootId, "root");

  useEffect(() => {
    if (minDuration.value !== undefined) {
      setMinDurationValue(minDuration.value ?? "");
    }
  }, [minDuration.value]);

  useEffect(() => {
    if (maxPayload.value !== undefined) {
      setMaxPayloadValue(maxPayload.value ?? "");
    }
  }, [maxPayload.value]);

  const thresholdsDirty = useMemo(
    () =>
      String(minDurationValue ?? "") !== String(minDuration.value ?? "") ||
      String(maxPayloadValue ?? "") !== String(maxPayload.value ?? ""),
    [maxPayload.value, maxPayloadValue, minDuration.value, minDurationValue],
  );

  const isLoading =
    minDuration.isLoading || maxPayload.isLoading || areConfigsLoading;

  const error = configsError;

  const handleSaveThresholds = async () => {
    await minDuration.updateSettings({
      "query-caching-min-duration-ms":
        minDurationValue === "" ? null : Number(minDurationValue),
      "query-caching-max-kb":
        maxPayloadValue === "" ? null : Number(maxPayloadValue),
    });
  };

  return (
    <SettingsPageWrapper
      title={t`Cache settings`}
      description={t`Control when query results are eligible to be written to the cache and configure the default caching policy.`}
    >
      <DelayedLoadingAndErrorWrapper error={error} loading={isLoading}>
        <Stack gap="xl">
          <SettingsSection
            title={t`Cache write thresholds`}
            description={t`Only write query results to the cache when they meet both the runtime and payload-size thresholds.`}
            maw="52rem"
            stackProps={{ gap: "md" }}
          >
            <Flex gap="xl" wrap="wrap" align="flex-start">
              <div style={{ flex: "1 1 20rem", maxWidth: "24rem" }}>
                <SettingHeader
                  id="query-caching-min-duration-ms"
                  title={t`Minimum execution time (ms)`}
                  description={t`Only cache results that run longer than this.`}
                />
                {minDuration.settingDetails?.is_env_setting &&
                minDuration.settingDetails?.env_name ? (
                  <SetByEnvVar varName={minDuration.settingDetails.env_name} />
                ) : (
                  <BasicAdminSettingInput
                    name="query-caching-min-duration-ms"
                    value={minDurationValue}
                    onChange={setMinDurationValue}
                    inputType="number"
                  />
                )}
              </div>
              <div style={{ flex: "1 1 20rem", maxWidth: "24rem" }}>
                <SettingHeader
                  id="query-caching-max-kb"
                  title={t`Maximum payload size (KB)`}
                  description={t`Skip caching results larger than this serialized size.`}
                />
                {maxPayload.settingDetails?.is_env_setting &&
                maxPayload.settingDetails?.env_name ? (
                  <SetByEnvVar varName={maxPayload.settingDetails.env_name} />
                ) : (
                  <BasicAdminSettingInput
                    name="query-caching-max-kb"
                    value={maxPayloadValue}
                    onChange={setMaxPayloadValue}
                    inputType="number"
                  />
                )}
              </div>
            </Flex>
            <Flex justify="flex-end">
              <Button
                onClick={handleSaveThresholds}
                disabled={
                  !thresholdsDirty ||
                  minDuration.updateSettingsResult.isLoading ||
                  maxPayload.updateSettingsResult.isLoading
                }
              >
                {t`Save`}
              </Button>
            </Flex>
          </SettingsSection>

          <SettingsSection
            title={t`Default policy`}
            description={t`Set the default caching policy used when a database does not override it.`}
            maw="52rem"
          >
            <StrategyForm
              targetId={rootId}
              targetModel="root"
              targetName={t`Default policy`}
              setIsDirty={() => undefined}
              saveStrategy={saveRootStrategy}
              savedStrategy={rootConfig?.strategy}
              shouldAllowInvalidation={false}
              shouldShowName={false}
              buttonLabels={{
                save: t`Save`,
                discard: t`Discard`,
              }}
            />
          </SettingsSection>
        </Stack>
      </DelayedLoadingAndErrorWrapper>
    </SettingsPageWrapper>
  );
};
